import { cn } from '@simplywallst/ui-core/next';
import { NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { Button } from '@simplywallst/ui-core/next';
import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { useEffect, useId, useRef, useState } from 'react';

export const ImageNodeWrapper = ({
  node,
  deleteNode,
  selected,
}: NodeViewProps) => {
  const { src, alt, title } = node.attrs;

  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const imageSrc = useCloudflareImage({
    src: src,
    width: 900,
    fit: 'scale-down',
  });
  const imageDragPreviewSrc = useCloudflareImage({
    src: src,
    width: 400,
    fit: 'scale-down',
    quality: 70,
    blur: 10,
  });

  const { srcSet: imageSrcSet } = getResponsiveImage({
    src: src,
    breakpoints: [1900, 1400, 800, 950, 700, 400],
  });

  useEffect(() => {
    if (!src) return;

    // Image loaded from cache
    if (imageRef.current?.complete) {
      setLoaded(true);
      setError(false);
      return;
    }
    setLoaded(false);
    setError(false);
  }, [src]);

  const isLoading = Boolean(src && !loaded && !error);
  const previewImageId = useId();

  return (
    <NodeViewWrapper className="relative">
      <img
        data-drag-handle
        data-drag-preview={previewImageId}
        ref={imageRef}
        src={imageSrc}
        srcSet={imageSrcSet}
        alt={alt}
        title={title}
        className={cn(
          'my-0 rounded-md overflow-hidden block mx-auto  border-solid border-[3px] border-transparent',
          selected && 'border-[3px] border-solid border-accent',
          isLoading || error ? 'opacity-0 h-[300px]' : 'opacity-100'
        )}
        onLoad={() => {
          setLoaded(true);
          setError(false);
        }}
        onError={() => setError(true)}
      />
      {(isLoading || error) && (
        <div
          role="presentation"
          aria-label="Image loading"
          className={cn(
            'absolute inset-0 bones min-h-[300px] w-full rounded-md overflow-hidden z-10 grid place-content-center',
            error ? '' : 'animate-pulse'
          )}
        >
          {error && <CrossSolid className="fill-bad-strong w-auto h-x10" />}
        </div>
      )}
      {selected && !isLoading && (
        <div className="absolute -bottom-x6 left-1/2 -translate-x-1/2 z-10">
          <Button
            variant="outline"
            className="bg-surface-2 hover:bg-surface-3 shadow-crisp"
            onClick={deleteNode}
            aria-label="Delete"
          >
            <BinSolid />
          </Button>
        </div>
      )}
      <img
        id={previewImageId}
        src={imageDragPreviewSrc}
        className="fixed left-[-99999px] top-[-99999px]"
      />
    </NodeViewWrapper>
  );
};
